import { useState, useEffect, useMemo, useRef } from 'react';
import { useStore, calculateDealScore } from '../store/useStore';
import { 
  Bot, 
  Calendar, 
  CheckCircle2, 
  Users, 
  ArrowRight,
  Sparkles,
  Zap
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { isToday } from 'date-fns';

export function AIQuickBoard() {
  const { leads, tasks, currentUser, aiName, aiTone, dataLoaded } = useStore();
  const navigate = useNavigate();
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Locks the briefing text the moment typing begins.
  // Subsequent store updates (background Supabase refresh) will never restart the animation.
  const lockedBriefing = useRef<string | null>(null);

  // ─── Data Aggregation ──────────────────────────────────────────────────────
  const agenda = useMemo(() => {
    const todayTasks = (tasks || []).filter(t => t && t.dueDate && isToday(new Date(t.dueDate)));
    const hotLeads = [...(leads || [])]
      .filter(l => l && l.status && !l.status.startsWith('closed'))
      .sort((a, b) => calculateDealScore(b) - calculateDealScore(a))
      .slice(0, 3);
    const completedToday = (tasks || []).filter(t => t && t.completedAt && isToday(new Date(t.completedAt))).length;
    const incomingToday = (leads || []).filter(l => l && l.createdAt && isToday(new Date(l.createdAt))).length;
    return { todayTasks, hotLeads, stats: { completedToday, incomingToday } };
  }, [leads, tasks]);

  // ─── Briefing Text (computed once when data is ready) ──────────────────────
  const fullBriefing = useMemo(() => {
    const name = currentUser?.name?.split(' ')[0] || 'Operative';
    const hour = new Date().getHours();
    
    // 1. Randomized Greetings
    const greetings = [
      `Good ${hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening'}, ${name}.`,
      `Welcome back to the bridge, ${name}.`,
      `System initialized. Ready for operations, ${name}.`,
      `The pipeline is active, ${name}. Here's the tactical update.`,
      `Systems green, ${name}. Standing by for commands.`,
    ];
    const greeting = greetings[Math.floor(Math.random() * greetings.length)];

    // 2. Dynamic Metrics
    const activeLeads = (leads || []).filter(l => l && !l.status?.startsWith('closed')).length;
    const negotiatingCount = (leads || []).filter(l => l.status === 'negotiating').length;
    const overdueTasks = (tasks || []).filter(t => t && t.dueDate && new Date(t.dueDate) < new Date() && !t.completedAt).length;
    const highPotentialCount = (leads || []).filter(l => calculateDealScore(l) > 80 && !l.status?.startsWith('closed')).length;
    const taskCount = agenda.todayTasks.length;
    
    // Stale leads logic: Not contacted in 14 days and not closed
    const fourteenDaysAgo = new Date();
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
    const staleLeads = (leads || []).filter(l => {
      if (l.status?.startsWith('closed')) return false;
      const lastContactDate = l.lastContact ? new Date(l.lastContact) : new Date(l.createdAt);
      return lastContactDate < fourteenDaysAgo;
    });

    // 3. Time-sensitive Contextual Messaging
    let contextualMsg = "";
    if (hour < 10) {
      contextualMsg = "Strike early—perfect time for outbound blitzes before the market heats up.";
    } else if (hour >= 17) {
      contextualMsg = "Closing time. Ensure all high-priority leads have a next-step locked in.";
    } else if (overdueTasks > 0) {
      contextualMsg = `Critical: You have ${overdueTasks} overdue task${overdueTasks > 1 ? 's' : ''} requiring immediate attention.`;
    } else if (staleLeads.length > 0) {
      contextualMsg = `Alert: ${staleLeads.length} lead${staleLeads.length > 1 ? 's haven\'t' : ' hasn\'t'} been contacted in over 2 weeks. Don't let them go cold.`;
    } else if (negotiatingCount > 0) {
      contextualMsg = `Tactical Focus: ${negotiatingCount} deal${negotiatingCount > 1 ? 's are' : ' is'} in late-stage negotiation. Push for the close.`;
    } else if (highPotentialCount > 0) {
      contextualMsg = `Target identified: ${highPotentialCount} high-potential lead${highPotentialCount > 1 ? 's' : ''} are ready for follow-up.`;
    } else {
      contextualMsg = taskCount > 0 
        ? `You've got ${taskCount} objective${taskCount > 1 ? 's' : ''} on the board for today.`
        : "The board is clear. Great time to hunt for new acquisitions.";
    }

    const fmt = (v: number) =>
      new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
    
    const activeValue = (leads || []).filter(l => !l.status?.startsWith('closed')).reduce((sum, l) => sum + (Number(l.estimatedValue) || 0), 0);

    let text = `${greeting} ${aiName} here. ${contextualMsg} Current active pipeline: ${fmt(activeValue)} across ${activeLeads} lead${activeLeads !== 1 ? 's' : ''}.`;
    
    if (aiTone.toLowerCase().includes('bold') || aiTone.toLowerCase().includes('assertive')) {
      text += " No excuses—let's dominate this sector.";
    }
    
    return text;
  }, [currentUser, aiName, aiTone, agenda, leads, tasks]);

  // ─── Typing Animation ──────────────────────────────────────────────────────
  // Waits for dataLoaded before starting. Locks message in a ref so that
  // subsequent store updates (background refresh) never restart the animation.
  useEffect(() => {
    if (!dataLoaded) return;               // wait for real data
    if (lockedBriefing.current !== null) return; // already started — don't restart

    lockedBriefing.current = fullBriefing; // lock now
    setDisplayText('');
    setIsTyping(true);
    let i = 0;
    const interval = setInterval(() => {
      const text = lockedBriefing.current!;
      setDisplayText(text.slice(0, i + 1));
      i++;
      if (i >= text.length) {
        clearInterval(interval);
        setIsTyping(false);
      }
    }, 22);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="liquid-glass border border-[var(--t-border-subtle)] rounded-3xl overflow-hidden shadow-xl mb-8 relative group">
      {/* Decorative background element */}
      <div className="absolute -top-24 -right-24 w-48 h-48 bg-[var(--t-primary)]/5 rounded-full blur-3xl group-hover:bg-[var(--t-primary)]/10 transition-colors duration-1000" />
      
      <div className="p-6 md:p-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Main Briefing Section */}
          <div className="flex-1 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-[var(--t-primary)] to-[var(--t-secondary)] p-[1px] shadow-lg shadow-[var(--t-primary)]/20">
                  <div className="w-full h-full rounded-2xl bg-[var(--t-surface)] flex items-center justify-center">
                    <Bot className="w-6 h-6 text-[var(--t-primary)]" />
                  </div>
                </div>
                <div>
                  <h3 className="font-black text-xl tracking-tight" style={{ color: 'var(--t-text)' }}>AI QuickBoard</h3>
                  <div className="flex items-center gap-2">
                    <span className="flex h-2 w-2 rounded-full bg-[var(--t-success)] animate-pulse" />
                    <p className="text-[var(--t-text-muted)] text-[10px] uppercase tracking-widest font-bold">System Online · {aiName}</p>
                  </div>
                </div>
              </div>

            <div className="relative">
              <p className="text-[var(--t-text)] leading-relaxed text-lg font-medium min-h-[80px]">
                {displayText || (!dataLoaded && <span style={{ opacity: 0.3 }}>Loading your briefing...</span>)}
                {isTyping && <span className="inline-block w-2 h-5 ml-1 bg-[var(--t-primary)] animate-pulse align-middle" />}
              </p>
            </div>

            <div className="flex flex-wrap gap-3 pt-6">
              <button 
                onClick={() => navigate('/leads?filter=hot')}
                className="px-4 py-2 rounded-xl bg-[var(--t-surface-hover)] border border-[var(--t-border)] text-[var(--t-text)] hover:border-[var(--t-primary)] text-[10px] font-bold uppercase tracking-wider transition-all shadow-sm"
              >
                Review Hot Leads
              </button>
              <button 
                onClick={() => navigate('/tasks')}
                className="px-4 py-2 rounded-xl bg-[var(--t-surface-hover)] border border-[var(--t-border)] text-[var(--t-text)] hover:border-[var(--t-primary)] text-[10px] font-bold uppercase tracking-wider transition-all shadow-sm"
              >
                Check Today's Tasks
              </button>
              <button 
                onClick={() => navigate('/sms')}
                className="px-4 py-2 rounded-xl bg-[var(--t-surface-hover)] border border-[var(--t-border)] text-[var(--t-text)] hover:border-[var(--t-primary)] text-[10px] font-bold uppercase tracking-wider transition-all shadow-sm"
              >
                Send Follow-up SMS
              </button>
            </div>

            <div className="flex flex-wrap gap-4 pt-4 border-t border-[var(--t-border)]/50">
              <div className="flex items-center gap-2 px-4 py-2 bg-[var(--t-surface-subtle)]/50 rounded-2xl border border-[var(--t-border-subtle)]">
                <CheckCircle2 className="w-4 h-4 text-[var(--t-success)]" />
                <span className="text-sm font-bold" style={{ color: 'var(--t-text)' }}>{agenda.stats.completedToday}</span>
                <span className="text-xs text-[var(--t-text-muted)]">Completed</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-[var(--t-surface-subtle)]/50 rounded-2xl border border-[var(--t-border-subtle)]">
                <Users className="w-4 h-4 text-[var(--t-info)]" />
                <span className="text-sm font-bold" style={{ color: 'var(--t-text)' }}>{agenda.stats.incomingToday}</span>
                <span className="text-xs text-[var(--t-text-muted)]">New Leads</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-[var(--t-surface-subtle)]/50 rounded-2xl border border-[var(--t-border-subtle)]">
                <Zap className="w-4 h-4 text-[var(--t-warning)]" />
                <span className="text-sm font-bold" style={{ color: 'var(--t-text)' }}>{(leads || []).length}</span>
                <span className="text-xs text-[var(--t-text-muted)]">In Pipeline</span>
              </div>
            </div>
          </div>

          {/* Actionable Column */}
          <div className="w-full md:w-80 space-y-4">
            <h4 className="text-[10px] font-black text-[var(--t-text-muted)] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-[var(--t-primary)]" />
              Priority Actions
            </h4>

            {/* Tasks Due Today */}
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-[var(--t-text-muted)] flex items-center gap-2">
                <Calendar className="w-3 h-3" /> Agenda Today
              </p>
              {agenda.todayTasks.length > 0 ? (
                agenda.todayTasks.slice(0, 2).map(task => (
                  <div key={task.id} className="p-3 rounded-2xl bg-[var(--t-surface)]/40 border border-[var(--t-border-subtle)] hover:border-[var(--t-primary)]/30 transition-colors cursor-pointer group/item"
                    onClick={() => navigate('/tasks')}
                  >
                    <p className="text-xs font-medium truncate" style={{ color: 'var(--t-text)' }}>{task.title}</p>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-[10px] text-[var(--t-text-muted)]">Due today</span>
                      <ArrowRight className="w-3 h-3 text-[var(--t-text-muted)] group-hover/item:translate-x-1 transition-transform" />
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-3 rounded-2xl border border-dashed border-[var(--t-border)] text-center">
                  <p className="text-[10px] text-[var(--t-text-muted)]">No tasks scheduled for today</p>
                </div>
              )}
            </div>

            {/* Hot Leads */}
            <div className="space-y-2 pt-2">
              <p className="text-[10px] font-bold text-[var(--t-text-muted)] flex items-center gap-2">
                <Zap className="w-3 h-3 text-[var(--t-warning)]" /> Hot Leads
              </p>
              {agenda.hotLeads.length > 0 ? (
                agenda.hotLeads.slice(0, 2).map(lead => (
                  <div key={lead.id} className="p-3 rounded-2xl bg-[var(--t-surface)]/40 border border-[var(--t-border-subtle)] hover:border-[var(--t-primary)]/30 transition-colors cursor-pointer group/item"
                    onClick={() => navigate(`/leads/${lead.id}/manage`)}
                  >
                    <p className="text-xs font-medium truncate" style={{ color: 'var(--t-text)' }}>{lead.name}</p>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-[10px] text-[var(--t-warning)] font-bold">Score: {calculateDealScore(lead)}</span>
                      <ArrowRight className="w-3 h-3 text-[var(--t-text-muted)] group-hover/item:translate-x-1 transition-transform" />
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-3 rounded-2xl border border-dashed border-[var(--t-border)] text-center">
                  <p className="text-[10px] text-[var(--t-text-muted)]">No active leads found</p>
                </div>
              )}
            </div>
            
            <button 
              onClick={() => navigate('/ai-test')}
              className="w-full mt-2 py-3 bg-[var(--t-primary)] hover:bg-[var(--t-primary-active)] text-white text-xs font-bold rounded-2xl transition-all shadow-lg shadow-[var(--t-primary)]/20 active:scale-[0.98]"
            >
              Consult {aiName} Assistant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
